import { merge } from 'lodash';
import { FiverrContext } from '@fiverr-private/fiverr_context';
import { BigQueryEventGroup, createBigQueryEvent } from '../../events';
import {
    BigQueryBannerEventType,
    GIG_IMPRESSION_SOURCE_PAGE,
} from './useEvents.constants';
import { UseEventsOptions } from './useEvents';

/**
 * Mixpanel event creator `createMixpanelBannerEvent` options interface.
 */
export interface CreateMixpanelBannerEventOptions
    extends Pick<UseEventsOptions, 'partnerSlug' | 'searchTerm'> {
    /**
     * Current user `id` from FiverrContext.
     */
    userId: FiverrContext['userId'];
}

/**
 * Create Mixpanel Banner event by params.
 */
export const createMixpanelBannerEvent = ({
    userId,
    searchTerm,
    partnerSlug,
}: CreateMixpanelBannerEventOptions) => ({
    'User ID': userId,
    'Search term': searchTerm,
    'Partner name': partnerSlug,
});

/**
 * BigQuery base event options creator `createBigQueryBannerEventBaseOptions` options interface.
 */
export interface CreateBigQueryBannerEventBaseOptions
    extends Pick<UseEventsOptions, 'partnerSlug' | 'pageName' | 'partnerId'> {
    /**
     * Fiverr business organization `id` from FiverrContext.
     */
    organizationId: FiverrContext['organizationId'];
}

/**
 * Create BigQuery Banner event base options.
 */
export const createBigQueryBannerEventBaseOptions = (
    type: BigQueryBannerEventType,
    {
        partnerSlug,
        partnerId,
        pageName,
        organizationId,
    }: CreateBigQueryBannerEventBaseOptions
) => ({
    type,
    page: {
        name: pageName,
        component: {
            type: partnerSlug,
        },
    },
    certified: {
        partner: {
            id: partnerId,
            name: partnerSlug,
        },
    },
    ...(organizationId && {
        organization: {
            id: organizationId,
        },
    }),
});

/**
 * BigQuery event creator `createBigQueryBannerClickEvent` options interface.
 */
export interface CreateBigQueryBannerClickEventOptions
    extends CreateBigQueryBannerEventBaseOptions {
    /**
     * Referral context `id`, value from query parameters if available.
     */
    refCtxId?: FiverrContext['queryParameters'][string];
}

/**
 * Create BigQuery Banner click event.
 */
export const createBigQueryBannerClickEvent = ({
    refCtxId,
    ...options
}: CreateBigQueryBannerClickEventOptions) => {
    const baseOptions = createBigQueryBannerEventBaseOptions(
        BigQueryBannerEventType.Click,
        options
    );

    return createBigQueryEvent(
        BigQueryEventGroup.Action,
        refCtxId
            ? merge(baseOptions, {
                  page: {
                      referrer: {
                          ctx_id: refCtxId,
                      },
                  },
              })
            : baseOptions
    );
};

/**
 * BigQuery event creator `createBigQueryBannerObserveEvent` options interface.
 */
export type CreateBigQueryBannerObserveEventOptions =
    CreateBigQueryBannerEventBaseOptions;

/**
 * Create BigQuery Banner observe event.
 */
export const createBigQueryBannerObserveEvent = (
    options: CreateBigQueryBannerObserveEventOptions
) => {
    const baseOptions = createBigQueryBannerEventBaseOptions(
        BigQueryBannerEventType.Show,
        options
    );

    return createBigQueryEvent(
        BigQueryEventGroup.Impressions,
        merge(baseOptions, {
            gig_impression: {
                sourcepage: GIG_IMPRESSION_SOURCE_PAGE,
            },
        })
    );
};
